import type { AnalyticsExportFormat } from '../components/analytics/AnalyticsExportMenu';
import type {
  NotificationDeliveryAnalyticsData,
  NotificationDeliveryChannelRow,
} from '../services/notificationDeliveryAnalyticsService';
import { openAnalyticsPrintWindow } from './analyticsExport';

export type { NotificationDeliveryFilters } from '../services/notificationDeliveryAnalyticsService';

export interface NotificationDeliveryExportMeta {
  startDate: string;
  endDate: string;
  eventCategoryLabel?: string;
  channelLabel?: string;
  statusLabel?: string;
  granularityLabel?: string;
}

function escapeCsv(value: string | number): string {
  return `"${String(value).replaceAll('"', '""')}"`;
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function downloadBlob(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function buildChannelTableHtml(
  rows: NotificationDeliveryChannelRow[],
  activeColumns: Array<{ key: keyof NotificationDeliveryChannelRow; label: string }>
): string {
  if (!rows.length) {
    return '<p>No channel data for the selected filters.</p>';
  }
  const header = activeColumns.map(column => `<th>${escapeHtml(column.label)}</th>`).join('');
  const body = rows
    .map(
      row =>
        `<tr>${activeColumns
          .map(column => `<td>${escapeHtml(row[column.key] ?? '')}</td>`)
          .join('')}</tr>`
    )
    .join('');
  return `<table>
    <thead>
      <tr>${header}</tr>
    </thead>
    <tbody>${body}</tbody>
  </table>`;
}

function buildReportHtml(
  data: NotificationDeliveryAnalyticsData,
  meta: NotificationDeliveryExportMeta,
  activeColumns: Array<{ key: keyof NotificationDeliveryChannelRow; label: string }>
): string {
  const { summary } = data;
  return `
    <p><strong>Period:</strong> ${escapeHtml(meta.startDate)} – ${escapeHtml(meta.endDate)}</p>
    ${meta.eventCategoryLabel ? `<p><strong>Event category:</strong> ${escapeHtml(meta.eventCategoryLabel)}</p>` : ''}
    ${meta.channelLabel ? `<p><strong>Channel:</strong> ${escapeHtml(meta.channelLabel)}</p>` : ''}
    ${meta.statusLabel ? `<p><strong>Delivery status:</strong> ${escapeHtml(meta.statusLabel)}</p>` : ''}
    ${meta.granularityLabel ? `<p><strong>Granularity:</strong> ${escapeHtml(meta.granularityLabel)}</p>` : ''}
    <h2>Summary</h2>
    <ul>
      <li>Total notifications sent: ${escapeHtml(summary.totalSent)}</li>
      <li>Delivery success rate: ${escapeHtml(summary.deliverySuccessRate)}%</li>
      <li>Pending notifications: ${escapeHtml(summary.pendingNotifications)}</li>
    </ul>
    <h2>Delivery by channel</h2>
    ${buildChannelTableHtml(data.channelRows, activeColumns)}
  `;
}

export function exportNotificationDeliveryAnalytics(
  format: AnalyticsExportFormat,
  data: NotificationDeliveryAnalyticsData,
  meta: NotificationDeliveryExportMeta,
  visibleColumnKeys?: Array<keyof NotificationDeliveryChannelRow>
) {
  const filenameBase = `notification-delivery-${meta.startDate}_${meta.endDate}`;
  const columns: Array<{ key: keyof NotificationDeliveryChannelRow; label: string }> = [
    { key: 'channel', label: 'Channel' },
    { key: 'total', label: 'Total sent' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'failed', label: 'Failed' },
    { key: 'pending', label: 'Pending' },
    { key: 'averageTime', label: 'Avg delivery time' },
  ];
  const activeColumns = visibleColumnKeys?.length
    ? columns.filter(column => visibleColumnKeys.includes(column.key))
    : columns;

  if (format === 'pdf') {
    openAnalyticsPrintWindow('Notification delivery', buildReportHtml(data, meta, activeColumns));
    return;
  }

  if (format === 'excel') {
    downloadBlob(
      `${filenameBase}.xls`,
      `<html><body>${buildReportHtml(data, meta, activeColumns)}</body></html>`,
      'application/vnd.ms-excel;charset=utf-8'
    );
    return;
  }

  const csvRows = [
    activeColumns.map(column => escapeCsv(column.label)),
    ...data.channelRows.map(row => activeColumns.map(column => escapeCsv(row[column.key] ?? ''))),
  ];
  downloadBlob(
    `${filenameBase}.csv`,
    csvRows.map(row => row.join(',')).join('\n'),
    'text/csv;charset=utf-8'
  );
}
